import React, { useEffect, useRef } from 'react';
import { weatherLayers, particleOverlays } from '../../../const/weatherLayer';
import { LayerGroup, TileLayer, useMap } from 'react-leaflet';
import axios from 'axios';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { message } from 'antd';
import 'leaflet-velocity/dist/leaflet-velocity';
import '@kngai/leaflet.timezones';
import L from 'leaflet';
import useMapSetting from '../../../zustand/useMap';
import { useLocation } from 'react-router-dom';
import VelocityTooltip from './VelocityTooltip';

const getLayerConfig = (layerName: string) => {
  return particleOverlays[layerName] || weatherLayers[layerName];
};

const MapOverlay: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
  const map = useMap();
  const {
    activeLayer,
    showWindLayer,
    showWaveLayer,
    showTimezoneLayer,
    mapType,
    setLoadingWindDirection,
    setLoadingWaveDirection,
    forecastDate,
  } = useMapSetting();
  const windLayerRef = useRef<any>(null);
  const waveLayerRef = useRef<any>(null);
  const timezoneLayerRef = useRef<any>(null);
  
  const windDataRef = useRef<any>(null);
  const waveDataRef = useRef<any>(null);
  
  const windDataFormattedRef = useRef<any>(null);
  const waveDataFormattedRef = useRef<any>(null);
  
  const [isWindDataReady, setIsWindDataReady] = React.useState(false);
  const [isWaveDataReady, setIsWaveDataReady] = React.useState(false);


  useEffect(() => {
    // map.invalidateSize();
    setTimeout(() => map.invalidateSize(), 300);
  }, [map]);

  useEffect(() => {
    if (!showWindLayer) {
      windDataFormattedRef.current = null;
      setIsWindDataReady(false);
      return;
    }

    const fetchData = async () => {
      setLoadingWindDirection(true);
      setIsWindDataReady(false);
      try {
        const dateToFetch = forecastDate || dayjs().subtract(1, 'day').format('YYYYMMDD');
        
        const res = await axios.get(
          `${process.env.REACT_APP_FORECAST_API_URL}/forecast`,
          {
            params: {
              date: dateToFetch,
              type: 'wind',
            },
          }
        );

        let dataFormatted = res.data.payload.map((item: any) => ({
          ...item,
          data: item.data.map((val: any) =>
            typeof val !== 'number' ? 0 : val
          ),
        }));

        windDataFormattedRef.current = dataFormatted;

        if (dataFormatted.length >= 2) {
          const uComponent = dataFormatted[0];
          const vComponent = dataFormatted[1];

          const interleavedData: number[] = [];
          for (let i = 0; i < uComponent.data.length; i++) {
            interleavedData.push(uComponent.data[i]);
            interleavedData.push(vComponent.data[i]);
          }

          windDataRef.current = {
            header: uComponent.header,
            data: interleavedData,
          };
        }

        setIsWindDataReady(true);
      } catch (error: any) {
        console.error('Failed get data forecast', error);
        setIsWindDataReady(false);
        
        // Show user-friendly message if NOAA data not available
        if (error?.response?.status === 404 && error?.response?.data?.message) {
          message.warning({
            content: `Wind Data: ${error.response.data.message}`,
            duration: 5,
            key: 'wind-noaa-unavailable',
          });
        }
      } finally {
        setLoadingWindDirection(false);
      }
    };

    fetchData();
  }, [showWindLayer, setLoadingWindDirection, forecastDate]);

  useEffect(() => {
    if (
      !showWindLayer ||
      !map ||
      !isWindDataReady ||
      !windDataFormattedRef.current
    ) {
      return;
    }

    if (windLayerRef.current) {
      map.removeLayer(windLayerRef.current);
      windLayerRef.current = null;
    }

    const colorScale =
      particleOverlays['Wind_Direction'].colorSchemes[mapType] ||
      particleOverlays['Wind_Direction'].colorSchemes.satellite;

    const windLayer = (L as any).velocityLayer({
      displayValues: true,
      displayOptions: {
        velocityType: 'Global Wind',
        position: 'bottomleft',
        emptyString: 'No wind velocity data',
      },
      data: windDataFormattedRef.current,
      lineWidth: 2,
      velocityScale: 0.02,
      opacity: 0.9,
      colorScale,
    });

    windLayer.addTo(map);
    windLayerRef.current = windLayer;

    return () => {
      if (windLayerRef.current) {
        map.removeLayer(windLayerRef.current);
        windLayerRef.current = null;
      }
    };
  }, [showWindLayer, map, mapType, isWindDataReady]);

  useEffect(() => {
    if (!showWaveLayer) {
      waveDataFormattedRef.current = null;
      setIsWaveDataReady(false);
      return;
    }

    const fetchData = async () => {
      setLoadingWaveDirection(true);
      setIsWaveDataReady(false);
      try {
        const dateToFetch = forecastDate || dayjs().subtract(1, 'day').format('YYYYMMDD');
        
        const res = await axios.get(
          `${process.env.REACT_APP_FORECAST_API_URL}/forecast`,
          {
            params: {
              date: dateToFetch,
              type: 'wave',
            },
          }
        );

        let dataFormatted;

        if (res.data.payload && res.data.payload.length >= 2) {
          const heightData = res.data.payload.find(
            (item: any) =>
              item.header?.parameterNumber === 3 ||
              item.header?.parameterNumberName?.includes('height') ||
              item.header?.parameterNumberName?.includes('significant') ||
              (item.header?.parameterCategory === 0 &&
                item.header?.parameterNumber === 3)
          );
          const directionData = res.data.payload.find(
            (item: any) =>
              item.header?.parameterNumber === 10 ||
              item.header?.parameterNumberName?.includes('direction') ||
              item.header?.parameterNumberName === 'Primary_wave_direction'
          );

          if (heightData && directionData) {
            waveDataRef.current = {
              header: heightData.header,
              heightData: heightData.data,
              directionData: directionData.data,
            };

            const uData: number[] = [];
            const vData: number[] = [];

            for (let i = 0; i < heightData.data.length; i++) {
              let height = heightData.data[i];
              let direction = directionData.data[i];

              if (height === 'NaN' || height === null || height === undefined) {
                height = 0;
              } else if (typeof height === 'string') {
                height = parseFloat(height);
              }

              if (
                direction === 'NaN' ||
                direction === null ||
                direction === undefined
              ) {
                direction = 0;
              } else if (typeof direction === 'string') {
                direction = parseFloat(direction);
              }

              if (
                typeof height === 'number' &&
                typeof direction === 'number' &&
                !isNaN(height) &&
                !isNaN(direction) &&
                height > 0.05
              ) {
                const radians = (direction * Math.PI) / 180;

                const scale = 0.8;
                const u = -height * Math.sin(radians) * scale;
                const v = -height * Math.cos(radians) * scale;

                uData.push(u);
                vData.push(v);
              } else {
                uData.push(0);
                vData.push(0);
              }
            }

            dataFormatted = [
              {
                header: {
                  ...heightData.header,
                  parameterCategory: 2,
                  parameterCategoryName: 'Momentum',
                  parameterNumber: 2,
                  parameterNumberName: 'u-component_of_wave',
                  parameterUnit: 'm s**-1',
                },
                data: uData,
              },
              {
                header: {
                  ...directionData.header,
                  parameterCategory: 2,
                  parameterCategoryName: 'Momentum',
                  parameterNumber: 3,
                  parameterNumberName: 'v-component_of_wave',
                  parameterUnit: 'm s**-1',
                },
                data: vData,
              },
            ];
          } else {
            console.error('Wave data missing height or direction components');
            return;
          }
        } else {
          dataFormatted = res.data.payload.map((item: any) => ({
            ...item,
            data: item.data.map((val: any) =>
              typeof val !== 'number' ? 0 : val
            ),
          }));
        }

        waveDataFormattedRef.current = dataFormatted;

        setIsWaveDataReady(true);
      } catch (error: any) {
        console.error('Failed get wave forecast data', error);
        setIsWaveDataReady(false);
        
        if (error?.response?.status === 404 && error?.response?.data?.message) {
          message.warning({
            content: `Wave Data: ${error.response.data.message}`,
            duration: 5,
            key: 'wave-noaa-unavailable',
          });
        }
      } finally {
        setLoadingWaveDirection(false);
      }
    };

    fetchData();
  }, [showWaveLayer, setLoadingWaveDirection, forecastDate]);

  useEffect(() => {
    if (
      !showWaveLayer ||
      !map ||
      !isWaveDataReady ||
      !waveDataFormattedRef.current
    ) {
      return;
    }

    const dataFormatted = waveDataFormattedRef.current;
    if (dataFormatted.length === 0) {
      return;
    }

    const firstData = dataFormatted[0];
    if (!firstData || !firstData.header || !firstData.data) {
      return;
    }

    if (waveLayerRef.current) {
      map.removeLayer(waveLayerRef.current);
      waveLayerRef.current = null;
    }

    const colorScale =
      particleOverlays['Wave_Direction'].colorSchemes[mapType] ||
      particleOverlays['Wave_Direction'].colorSchemes.satellite;

    const waveLayer = (L as any).velocityLayer({
      displayValues: true,
      displayOptions: {
        velocityType: 'Ocean Waves',
        position: 'bottomleft',
        emptyString: 'No wave data',
        displayEmptyString: 'No wave data',
      },
      data: dataFormatted,
      lineWidth: 7,
      velocityScale: 0.03,
      opacity: 0.98,
      particleAge: 80,
      particleMultiplier: 1 / 150,
      minVelocity: 0.01,
      maxVelocity: 15,
      frameRate: 10,
      colorScale,
    });

    waveLayer.addTo(map);
    waveLayerRef.current = waveLayer;

    return () => {
      if (waveLayerRef.current) {
        map.removeLayer(waveLayerRef.current);
        waveLayerRef.current = null;
      }
    };
  }, [showWaveLayer, map, mapType, isWaveDataReady]);

  useEffect(() => {
    if (showTimezoneLayer) {
      const timezoneLayer = (L as any).timezones.bindPopup(function (
        layer: any
      ) {
        const timeZone = layer.feature.properties.tz_name1st;

        // Check if the time zone is valid
        if (!timeZone || timeZone === null) {
          return 'Invalid time zone specified: null';
        }

        try {
          // Attempt to format the date with the specified time zone
          return new Date().toLocaleString('en-GB', {
            timeZone: timeZone,
            timeZoneName: 'short',
          });
        } catch (e) {
          // Handle invalid time zone errors
          return `Invalid time zone specified: ${timeZone}`;
        }
      });
      timezoneLayer.addTo(map);
      timezoneLayerRef.current = timezoneLayer;
    }

    return () => {
      if (timezoneLayerRef.current) {
        map.removeLayer(timezoneLayerRef.current);
      }
    };
  }, [showTimezoneLayer, map]);

  const legendToShow = activeLayer
    ? activeLayer
    : showWaveLayer
    ? 'Wave_Direction'
    : showWindLayer
    ? 'Wind_Direction'
    : null;

  return (
    <>
      {legendToShow && (
        <WeatherLayerGroup activeLayer={legendToShow} mapType={mapType} />
      )}

      {!isMobile && (
        <VelocityTooltip
          showWindLayer={showWindLayer}
          showWaveLayer={showWaveLayer}
          windDataRef={windDataRef}
          waveDataRef={waveDataRef}
          isWindDataReady={isWindDataReady}
          isWaveDataReady={isWaveDataReady}
        />
      )}
    </>
  );
};

const WeatherLayerGroup: React.FC<{
  activeLayer: string;
  mapType?: any;
}> = ({ activeLayer, mapType }) => {
  const { pathname } = useLocation();
  
  const layerConfig = getLayerConfig(activeLayer);
  
  const getLayerColors = () => {
    if ((activeLayer === 'Wave_Direction' || activeLayer === 'Wind_Direction') && mapType) {
      return layerConfig.colorSchemes[mapType] || layerConfig.colorSchemes.satellite;
    }
    return layerConfig.colors;
  };

  const layerColors = getLayerColors();

  return (
    <LayerGroup>
      {layerConfig.url && (
        <TileLayer url={layerConfig.url} attribution='&copy; OpenWeatherMap' />
      )}

      <ContainerScale
        style={{
          bottom: pathname.includes('/home') ? '68px' : '10px',
        }}
      >
        <WrapperScale>
          <ScaleTitle>
            {activeLayer.split('_').join(' ')} Scale, ({layerConfig.unit})
          </ScaleTitle>
          <div>
            <ScaleRange>
              {layerConfig.labels.map((label: any, index: any) => (
                <div key={index}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '2px',
                    }}
                  >
                    <span style={{ fontSize: 8 }}>{label}</span>
                  </div>
                </div>
              ))}
            </ScaleRange>
            <ScaleIndicator colors={layerColors} />
          </div>
        </WrapperScale>
      </ContainerScale>
    </LayerGroup>
  );
};

export default MapOverlay;

const ContainerScale = styled.div`
  position: absolute;
  bottom: 68px;
  right: 210px;
  padding: 5px;
  border-radius: 5px;
  font-size: 12px;
  line-height: 1.5;
  z-index: 1099;
  background: white;

  @media (max-width: 767px) {
    left: 12px;
    right: auto;
    bottom: 56px !important;
    width: 112px;
    max-width: 112px;
    padding: 8px;
    border-radius: 8px;
    font-size: 10px;
  }
`;

const WrapperScale = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }
`;

const ScaleTitle = styled.div`
  font-size: 10px;

  @media (max-width: 767px) {
    font-size: 9px;
    line-height: 12px;
    text-align: center;
  }
`;

const ScaleRange = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 200px;

  @media (max-width: 767px) {
    width: 100%;
    font-size: 7px;
  }
`;

const ScaleIndicator = styled.div<{
  colors: string;
}>`
  height: 5px;
  width: 200px;
  background: ${(props) => `linear-gradient(90deg, ${props.colors});`};

  @media (max-width: 767px) {
    width: 100%;
    height: 8px;
    border-radius: 4px;
  }
`;
